import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { removeFromCart, clearCart, updateQuantity } from "../../redux/cartSlice";
import { setCurrency } from "../../redux/currencySlice";
import apiCart from "../../apiProvider/addToCartApi";
import apiProvider from "../../apiProvider/api";
import { IMAGE_URL } from "../../network/apiClient";
import { useCustomToast } from "../../context/CustomToastContext";
import { useRef } from "react";

const CartPage = () => {
    const dispatch = useDispatch();
    const [cartItems, setCartItems] = useState([]);
    const [cartId, setCartId] = useState(null);
    const [selectedCurrencyDetails, setSelectedCurrencyDetails] = useState(null);
    const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
    const [apiCartData, setApiCartData] = useState({
        subtotal: 0,
        total: 0,
        deliveryCharge: 0,
        savedAmount: 0,
        customerTotalTax: 0
    });
    const [loading, setLoading] = useState(true);
    const { showToast } = useCustomToast();
    const prevCurrencyRef = useRef(selectedCurrency);

    useEffect(() => {
        const syncAndFetch = async () => {
            // Only trigger update if currency has actually changed and we have a cart
            if (cartId && selectedCurrency && prevCurrencyRef.current?.value !== selectedCurrency.value) {
                const result = await apiCart.updateCartCurrency(cartId, selectedCurrency.value);
                
                if (!result.status) {
                    // Get the error message from the API response
                    const errorMsg = result.error || (result.response && typeof result.response === 'string' ? result.response : "Pricing not available for some products in this currency.");
                    showToast(errorMsg, "error");
                    
                    // Revert Redux state to previous currency
                    // This will also update the Header dropdown
                    if (prevCurrencyRef.current) {
                        dispatch(setCurrency(prevCurrencyRef.current));
                    }
                    return; // Re-dispatch will trigger this effect again, we handle it there
                }
            }
            
            // If success or first load, update the ref and fetch details
            prevCurrencyRef.current = selectedCurrency;
            getCartDetails();
        };
        syncAndFetch();
    }, [selectedCurrency, cartId]);



    const handleQuantityChange = async (productCartId, newQty) => {
        if (newQty >= 1) {
            // Find the item to be updated
            const itemToUpdate = cartItems.find(item => item.id === productCartId);

            if (!itemToUpdate) return;

            // Update local state immediately for better UX
            setCartItems(prevItems =>
                prevItems.map(item =>
                    item.id === productCartId ? { ...item, quantity: parseInt(newQty) } : item
                )
            );

            // Call API to update quantity on server using addToCart logic
            try {
                const token = localStorage.getItem('userToken');
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const guestId = localStorage.getItem('guestUserId');

                const price = Number(itemToUpdate.mrpPrice || 0);
                const totalPrice = price * newQty;

                const productObj = {
                    productId: itemToUpdate.productId,
                    quantity: newQty,
                    mrpPrice: price,
                    variantId: itemToUpdate.variantId,
                    ...itemToUpdate.attributes
                };

                let payload = {
                    products: [productObj],
                    total: totalPrice,
                    subtotal: totalPrice,
                    currency: selectedCurrency?.value,
                };

                if (token && user._id) {
                    payload.type = 'user';
                    payload.userId = user._id;
                    payload.userType = 'user';
                } else if (guestId) {
                    payload.type = 'guest';
                    payload.guestUserId = guestId;
                    payload.userType = 'guest';
                }

                await apiCart.addToCart(payload);

                // Update global state for header count
                dispatch(updateQuantity({
                    id: productCartId,
                    quantity: newQty // API call succeeded, so we can update global state
                }));

                // Refresh cart to get updated totals
                await getCartDetails();
            } catch (error) {
                console.error("Error updating quantity:", error);
                // Revert on error
                await getCartDetails();
            }
        }
    };

    const handleRemoveItem = async (productId, offer) => {
        try {
            if (!cartId) {
                console.error("Cart ID not found");
                return;
            }
            const result = await apiCart.deleteCart(cartId, productId, offer);
            if (result.status) {
                // Refresh cart after removal
                await getCartDetails();
                dispatch(removeFromCart(productId));
            } else {
                console.error("Failed to remove item:", result.response);
            }
        } catch (error) {
            console.error("Error removing item:", error);
        }
    };

    const getCartDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const guestId = localStorage.getItem('guestUserId');

            let result;
            if (token && user._id) {
                result = await apiCart.getCart(user._id, { type: 'user', userType: 'user' });
            } else if (guestId) {
                result = await apiCart.getCart(guestId, { type: 'guest', userType: 'guest' });
            }

            if (result && result.response?.data && result.response.data.length > 0) {
                const apiData = result.response.data[0];
                setCartId(apiData._id);
                setSelectedCurrencyDetails(apiData.currencyDetails);

                const getProductStock = (product) => {
                    if (!product.customerAttribute || !product.variantId) return 0;
                    const variant = product.customerAttribute.find(v => v._id === product.variantId);
                    if (variant) return variant.stock || 0;
                    for (const attr of product.customerAttribute) {
                        if (attr.materials) {
                            const material = attr.materials.find(m => m._id === product.variantId);
                            if (material) return material.stock || 0;
                        }
                    }
                    return 0;
                };

                const mappedItems = apiData.products.map((product) => {
                    const stock = getProductStock(product);
                    const isOutOfStock = stock <= 0 || product.quantity > stock;
                    return {
                        id: product.productCartId || product._id,
                        productId: product._id,
                        name: product.productName,
                        partNo: product.attributes?.variantName || 'N/A',
                        attributes: product.attributes || {},
                        variantId: product.variantId,
                        totalAmount: product.totalAmount,
                        mrpPrice: product.mrpPrice,
                        quantity: product.quantity,
                        image: product.productImage && product.productImage.length > 0
                            ? `${IMAGE_URL}${product.productImage[0].docPath}/${product.productImage[0].docName}`
                            : "/img/product-seal-1.png",
                        offerAmount: product.offerAmount || 0,
                        offerType: product.offerType || 'no',
                        isOutOfStock: isOutOfStock,
                        stock: stock
                    };
                });

                setCartItems(mappedItems);

                // Recalculate totals for in-stock items only
                const inStockItems = mappedItems.filter(item => !item.isOutOfStock);
                const newSubtotal = inStockItems.reduce((acc, item) => acc + (item.totalAmount || 0), 0);
                const deliveryCharge = apiData.deliveryCharge || 0;
                const savedAmount = inStockItems.reduce((acc, item) => acc + (item.offerAmount || 0), 0);
                const tax = apiData.customerTotalTax || apiData.wholesalerTotalTax || 0; // Assuming tax is already calculated for standard items or handled by API

                setApiCartData({
                    subtotal: newSubtotal,
                    total: newSubtotal + deliveryCharge + (apiData.customerTotalTax || 0),
                    deliveryCharge: deliveryCharge,
                    savedAmount: savedAmount,
                    customerTotalTax: apiData.customerTotalTax || apiData.wholesalerTotalTax || 0
                });
            } else {
                setCartItems([]);
            }
        } catch (error) {
            console.error("Error fetching cart details:", error);
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="cart-page-wrapper" style={{ backgroundColor: '#e8e4dc', minHeight: '100vh', paddingTop: '80px', paddingBottom: '60px' }}>
                <div className="container text-center py-5">
                    <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading your cart...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-page-wrapper" style={{ backgroundColor: '#e8e4dc', minHeight: '100vh', paddingTop: '180px', paddingBottom: '60px' }}>
            <div className="container" style={{ maxWidth: '1400px' }}>
                {/* Page Title */}
                <div className="text-center mb-5 pt-4">
                    <h1 className="shopping-bag-title">SHOPPING BAG</h1>
                </div>

                {cartItems.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <i className="bi bi-cart3 opacity-25" style={{ fontSize: '5rem' }}></i>
                        </div>
                        <h3 className="fw-bold text-dark mb-3">Your cart is empty</h3>
                        <p className="text-muted mb-4">Looks like you haven't added any products yet.</p>
                        <Link to="/products" className="btn btn-dark px-5 py-3 text-uppercase fw-bold">
                            Continue Shopping
                        </Link>
                    </div>
                ) : (
                    <div className="row g-4">
                        {/* Left Column: Cart Items Table */}
                        <div className="col-lg-8">
                            <div className="cart-table-wrapper bg-white" style={{ padding: '20px 24px', borderRadius: "8px" }}>
                                {/* Table Header */}
                                <div className="table-header d-flex align-items-center pb-3 mb-2" style={{ borderBottom: '2px solid #333' }}>
                                    <div style={{ width: '35%', paddingLeft: '80px' }}>
                                        <span className="text-uppercase fw-semibold" style={{ fontSize: '13px', letterSpacing: '1.2px', color: '#000' }}>PRODUCT</span>
                                    </div>
                                    <div style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="text-uppercase fw-semibold" style={{ fontSize: '13px', letterSpacing: '1.2px', color: '#000' }}>UNIT PRICE</span>
                                    </div>
                                    <div style={{ width: '18%', textAlign: 'center' }}>
                                        <span className="text-uppercase fw-semibold" style={{ fontSize: '13px', letterSpacing: '1.2px', color: '#000' }}>QUANTITY</span>
                                    </div>
                                    <div style={{ width: '17%', textAlign: 'center' }}>
                                        <span className="text-uppercase fw-semibold" style={{ fontSize: '13px', letterSpacing: '1.2px', color: '#000' }}>TOTAL</span>
                                    </div>
                                    <div style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="text-uppercase fw-semibold" style={{ fontSize: '13px', letterSpacing: '1.2px', color: '#000' }}>ACTION</span>
                                    </div>
                                </div>

                                {/* Cart Items */}
                                {cartItems.map((item) => (
                                    <div key={item.id} className="cart-item-row" style={{ borderBottom: '1px solid #eee', padding: '20px 0', opacity: item.isOutOfStock ? 0.6 : 1 }}>
                                        {/* Desktop View (lg & above) */}
                                        <div className="d-none d-lg-flex align-items-center">
                                            <div className="d-flex align-items-center" style={{ width: '35%' }}>
                                                <div className="product-image-container me-3">
                                                    <img src={item.image} alt={item.name} className="img-fluid" />
                                                </div>
                                                <div className="product-info">
                                                    <h6 className="product-name">{item.name}</h6>
                                                    <p className="product-sku">{item.partNo}</p>
                                                    {item.isOutOfStock && <span className="stock-badge out-of-stock">Out of Stock</span>}
                                                </div>
                                            </div>
                                            <div style={{ width: '15%', textAlign: 'center' }}>
                                                <span className="unit-price">{selectedCurrencyDetails?.symbol || '₹'}{item.mrpPrice}</span>
                                            </div>
                                            <div style={{ width: '18%', textAlign: 'center' }}>
                                                <div className="modern-quantity-selector mx-auto">
                                                    <button className="quantity-btn" onClick={() => handleQuantityChange(item.id, item.quantity - 1)} disabled={item.quantity <= 1 || item.isOutOfStock}>−</button>
                                                    <span className="quantity-value">{item.quantity}</span>
                                                    <button className="quantity-btn" onClick={() => handleQuantityChange(item.id, item.quantity + 1)} disabled={item.isOutOfStock || item.quantity >= item.stock}>+</button>
                                                </div>
                                            </div>
                                            <div style={{ width: '17%', textAlign: 'center' }}>
                                                <span className="unit-price fw-bold">{selectedCurrencyDetails?.symbol || '₹'}{item.totalAmount}</span>
                                            </div>
                                            <div style={{ width: '15%', textAlign: 'center' }}>
                                                <button className="btn btn-link text-danger text-decoration-none fw-medium" onClick={() => handleRemoveItem(item.id, item.offerType)}>REMOVE</button>
                                            </div>
                                        </div>

                                        {/* Mobile/Tablet View (below lg) */}
                                        <div className="d-lg-none">
                                            <div className="d-flex gap-3 mb-3">
                                                <div className="product-image-container" style={{ width: '80px', height: '80px' }}>
                                                    <img src={item.image} alt={item.name} className="img-fluid" />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="product-name mb-1">{item.name}</h6>
                                                    <p className="product-sku mb-2">{item.partNo}</p>
                                                    <div className="d-flex justify-content-between align-items-end">
                                                        <div className="modern-quantity-selector">
                                                            <button className="quantity-btn" style={{ padding: '4px 10px' }} onClick={() => handleQuantityChange(item.id, item.quantity - 1)} disabled={item.quantity <= 1 || item.isOutOfStock}>-</button>
                                                            <span className="quantity-value" style={{ width: '30px' }}>{item.quantity}</span>
                                                            <button className="quantity-btn" style={{ padding: '4px 10px' }} onClick={() => handleQuantityChange(item.id, item.quantity + 1)} disabled={item.isOutOfStock || item.quantity >= item.stock}>+</button>
                                                        </div>
                                                        <div className="text-end">
                                                            <div className="text-muted small mb-1">Total</div>
                                                            <div className="unit-price fw-bold" style={{ fontSize: '18px' }}>{selectedCurrencyDetails?.symbol || '₹'}{item.totalAmount}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center border-top pt-2">
                                                <span className="text-muted" style={{ fontSize: '13px' }}>Price: {selectedCurrencyDetails?.symbol || '₹'}{item.mrpPrice}</span>
                                                <button className="btn btn-link text-danger text-decoration-none p-0 fw-medium" style={{ fontSize: '14px' }} onClick={() => handleRemoveItem(item.id, item.offerType)}>REMOVE</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Column: Order Summary */}
                        <div className="col-lg-4">
                            <div className="order-summary-wrapper bg-white sticky-top" style={{ padding: '40px', top: '100px', borderRadius: '8px' }}>
                                <h5 className="text-uppercase mb-4" style={{ fontSize: '18px', letterSpacing: '2px', fontWeight: '600' }}>
                                    ORDER SUMMARY
                                </h5>

                                {/* Currency Selector */}
                                {/* <div className="mb-4">
                                    <label className="form-label text-uppercase fw-semibold" style={{ fontSize: '12px', color: '#666' }}>Currency</label>
                                    <select
                                        className="form-select"
                                        style={{ borderRadius: '4px', fontSize: '14px', border: '1px solid #ddd' }}
                                        value={selectedCurrency?._id || ""}
                                        onChange={handleCurrencyChange}
                                    >
                                        {currencies.map(curr => (
                                            <option key={curr._id} value={curr._id}>
                                                {curr.code} ({curr.symbol})
                                            </option>
                                        ))}
                                    </select>
                                </div> */}

                                {/* Subtotal */}
                                <div className="d-flex justify-content-between mb-3">
                                    <span style={{ fontSize: '15px', color: '#666' }}>Subtotal</span>
                                    <span style={{ fontSize: '15px', color: '#333' }}>{selectedCurrencyDetails?.symbol || '₹'} {apiCartData.subtotal}</span>
                                </div>

                                {/* Tax (if applicable) */}
                                {apiCartData.customerTotalTax > 0 && (
                                    <div className="d-flex justify-content-between mb-3">
                                        <span style={{ fontSize: '15px', color: '#666' }}>Tax</span>
                                        <span style={{ fontSize: '15px', color: '#333' }}>{selectedCurrencyDetails?.symbol || '₹'} {apiCartData.customerTotalTax}</span>
                                    </div>
                                )}

                                {/* Shipping */}
                                <div className="d-flex justify-content-between pb-3 mb-3" style={{ borderBottom: '1px solid #e0e0e0' }}>
                                    <span style={{ fontSize: '15px', color: '#666' }}>Shipping</span>
                                    <span style={{ fontSize: '15px', color: apiCartData.deliveryCharge === 0 ? '#27ae60' : '#333', fontWeight: '500' }}>
                                        {apiCartData.deliveryCharge === 0 ? 'Free Delivery' : `${selectedCurrencyDetails?.symbol || '₹'} ${apiCartData.deliveryCharge}`}
                                    </span>
                                </div>

                                {/* Savings (if applicable) */}
                                {apiCartData.savedAmount > 0 && (
                                    <div className="d-flex justify-content-between mb-3" style={{ color: '#27ae60' }}>
                                        <span style={{ fontSize: '15px' }}>You Save</span>
                                        <span style={{ fontSize: '15px', fontWeight: '500' }}>{selectedCurrencyDetails?.symbol || '₹'} {apiCartData.savedAmount}</span>
                                    </div>
                                )}

                                {/* Total */}
                                <div className="d-flex justify-content-between mb-4 pb-4" style={{ borderBottom: '2px solid #333' }}>
                                    <span className="text-uppercase fw-bold" style={{ fontSize: '16px', letterSpacing: '1px' }}>TOTAL</span>
                                    <span className="fw-bold" style={{ fontSize: '18px' }}>{selectedCurrencyDetails?.symbol || '₹'} {apiCartData.total}</span>
                                </div>

                                {/* Checkout Button */}
                                <Link
                                    to={cartItems.every(item => item.isOutOfStock) ? "#" : "/checkout"}
                                    className={`btn btn-dark w-100 text-uppercase fw-bold mb-3 text-decoration-none ${cartItems.every(item => item.isOutOfStock) ? 'disabled opacity-50' : ''}`}
                                    style={{
                                        padding: '18px',
                                        letterSpacing: '2px',
                                        fontSize: '14px',
                                        pointerEvents: cartItems.every(item => item.isOutOfStock) ? 'none' : 'auto'
                                    }}
                                >
                                    CHECKOUT
                                </Link>

                                {/* Continue Shopping Link */}
                                <Link to="/products" className="btn btn-outline-dark w-100 text-uppercase fw-medium mb-3" style={{ padding: '14px', letterSpacing: '1px', fontSize: '13px' }}>
                                    Continue Shopping
                                </Link>

                                {/* Security Note */}
                                <p className="text-center mb-0" style={{ fontSize: '12px', color: '#999' }}>
                                    Secure Checkout - SSL Encrypted
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .shopping-bag-title {
                    font-size: 56px;
                    font-weight: 700;
                    letter-spacing: 12px;
                    color: #1a1a1a;
                    margin: 0;
                    font-family: 'Playfair Display', Georgia, serif;
                    text-transform: uppercase;
                }

                .product-image-container {
                    width: 100px;
                    height: 100px;
                    background: #fff;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                    flex-shrink: 0;
                    border-radius: 12px;
                }

                .product-name {
                    font-size: 16px;
                    font-weight: 600;
                    color: #2d3748;
                    margin-bottom: 4px;
                    line-height: 1.4;
                }

                .product-sku {
                    font-size: 13px;
                    color: #718096;
                    margin-bottom: 0;
                }

                .unit-price, .total-price {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1a202c;
                }

                .total-price {
                    color: var(--primary-color);
                }

                .stock-badge {
                    font-size: 10px;
                    padding: 2px 8px;
                    border-radius: 4px;
                    text-transform: uppercase;
                    font-weight: 700;
                    display: inline-block;
                }

                .out-of-stock {
                    background: #fff5f5;
                    color: #c53030;
                    border: 1px solid #feb2b2;
                }

                .btn-remove {
                    background: #fff5f5;
                    color: #c53030;
                    border: 1px solid transparent;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .btn-remove:hover {
                    background: #c53030;
                    color: #fff;
                    transform: scale(1.05);
                }

                .mobile-label {
                    font-size: 10px;
                    font-weight: 700;
                    color: #a0aec0;
                    letter-spacing: 1px;
                    margin-bottom: 4px;
                }

                .modern-quantity-selector {
                    display: inline-flex;
                    align-items: center;
                    background: #edf2f7;
                    border-radius: 8px;
                    padding: 2px;
                }

                .quantity-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border: none;
                    border-radius: 6px;
                    color: #4a5568;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .quantity-btn:hover:not(:disabled) {
                    background: #fff;
                    color: var(--primary-color);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }

                .quantity-value {
                    width: 32px;
                    text-align: center;
                    font-weight: 600;
                    font-size: 14px;
                    color: #2d3748;
                }

                @media (max-width: 991px) {
                    .shopping-bag-title {
                        font-size: 40px;
                        letter-spacing: 6px;
                    }

                    .cart-table-wrapper {
                        background: transparent !important;
                        padding: 0 !important;
                        box-shadow: none !important;
                    }

                    .cart-item-row {
                        background: #fff;
                        padding: 20px !important;
                        border-radius: 16px;
                        margin-bottom: 16px;
                        border: none !important;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                    }

                    .product-image-container {
                        width: 80px;
                        height: 80px;
                    }

                    .btn-remove {
                        width: auto;
                        height: auto;
                        padding: 8px 16px;
                        border: 1px solid #feb2b2;
                        border-radius: 8px;
                        margin-top: 15px;
                    }
                }
            `}</style>
        </div>
    );
};

export default CartPage;